import React, { useEffect } from 'react'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import { ORDER_EDITING_DISABLED_PHONE as CUSTOMER_SERVICE_PHONE } from '@/config/maintenance'
import '@/styles/components/confirm-modal.css'

export interface ScheduleInfo {
  title: string
  intro: string
  notes: string[]
}

/**
 * DF-478: the "More info" for a day in the Christmas schedule ("Your Festive
 * delivery"). A notice, so one button and no cancel, like the other notice
 * modals. The Customer Service number can be tapped.
 */
const ScheduleInfoModal: React.FC<{ info: ScheduleInfo | null, onClose: () => void }> = ({ info, onClose }) => {
  useEffect(() => {
    if (!info) return undefined
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    const releaseScroll = lockBodyScroll()

    return () => {
      document.removeEventListener('keydown', handleEscape)
      releaseScroll()
    }
  }, [info, onClose])

  if (!info) return null

  const withPhone = (text: string) => {
    const [before, after] = text.split(CUSTOMER_SERVICE_PHONE)
    if (after === undefined) return text
    return (
      <>
        {before}
        <a href={`tel:${CUSTOMER_SERVICE_PHONE.replace(/\s/g, '')}`}>{CUSTOMER_SERVICE_PHONE}</a>
        {after}
      </>
    )
  }

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-container confirm-modal schedule-info-modal" role="dialog" aria-modal="true" aria-labelledby="schedule-info-title">
        <div className="modal-header">
          <h2 id="schedule-info-title">{info.title}</h2>
          <button className="modal-close-floating" onClick={onClose} aria-label="Close">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-content">
          <p className="confirm-message">{info.intro}</p>
          <p className="schedule-info-subtitle">Please note:</p>
          <ul className="schedule-info-notes">
            {info.notes.map(note => <li key={note}>{withPhone(note)}</li>)}
          </ul>
        </div>

        <div className="modal-footer">
          <button className="btn-confirm" onClick={onClose} autoFocus>
            OK
          </button>
        </div>
      </div>
    </div>
  )
}

export default ScheduleInfoModal
